import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PowerUpsPageDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PowerUpsPage"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"StringValue","value":"me","block":false}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"organizations"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"idMember"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"plugins"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"filter"},"value":{"kind":"EnumValue","value":"editable"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"icon"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}}]}},{"kind":"Field","name":{"kind":"Name","value":"iframeConnectorUrl"}},{"kind":"Field","name":{"kind":"Name","value":"listings"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"locale"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"public"}}]}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PowerUpsPage","document":PowerUpsPageDocument}} as const;
export type PowerUpsPageQueryVariables = Types.Exact<{ [key: string]: never; }>;


export type PowerUpsPageQuery = (
  { __typename: 'Query' }
  & { member?: Types.Maybe<(
    { __typename: 'Member' }
    & Pick<Types.Member, 'id'>
    & { organizations: Array<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'id' | 'displayName' | 'name'>
      & { memberships: Array<(
        { __typename: 'Organization_Membership' }
        & Pick<Types.Organization_Membership, 'id' | 'idMember' | 'memberType'>
      )>, plugins: Array<(
        { __typename: 'Plugin' }
        & Pick<Types.Plugin, 'id' | 'iframeConnectorUrl' | 'name' | 'public'>
        & { icon: (
          { __typename: 'Plugin_Icon' }
          & Pick<Types.Plugin_Icon, 'url'>
        ), listings?: Types.Maybe<Array<(
          { __typename: 'Plugin_Listings' }
          & Pick<Types.Plugin_Listings, 'id' | 'locale' | 'name'>
        )>> }
      )> }
    )> }
  )> }
);

/**
 * __usePowerUpsPageQuery__
 *
 * To run a query within a React component, call `usePowerUpsPageQuery` and pass it any options that fit your needs.
 * When your component renders, `usePowerUpsPageQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePowerUpsPageQuery({
 *   variables: {
 *   },
 * });
 */
export function usePowerUpsPageQuery(
  baseOptions?: TrelloQueryHookOptions<
    PowerUpsPageQuery,
    PowerUpsPageQueryVariables
  >,
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PowerUpsPageDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<PowerUpsPageQuery, PowerUpsPageQueryVariables>(
    PowerUpsPageDocument,
    options,
  );
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePowerUpsPageLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PowerUpsPageQuery,
    PowerUpsPageQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<PowerUpsPageQuery, PowerUpsPageQueryVariables>(
    PowerUpsPageDocument,
    options,
  );
}
export function usePowerUpsPageSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PowerUpsPageQuery,
        PowerUpsPageQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<PowerUpsPageQuery, PowerUpsPageQueryVariables>(
    PowerUpsPageDocument,
    options,
  );
}
export type PowerUpsPageQueryHookResult = ReturnType<
  typeof usePowerUpsPageQuery
>;
export type PowerUpsPageLazyQueryHookResult = ReturnType<
  typeof usePowerUpsPageLazyQuery
>;
export type PowerUpsPageSuspenseQueryHookResult = ReturnType<
  typeof usePowerUpsPageSuspenseQuery
>;
export type PowerUpsPageQueryResult = Apollo.QueryResult<
  PowerUpsPageQuery,
  PowerUpsPageQueryVariables
>;
